import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api/client';
import {
  isDataUpdateConflict,
  isRevisionConflict,
  isUnknownOutcome,
  startCustomDashboardDataUpdate,
  type QueuedDataUpdate,
} from '@/lib/api/custom-dashboard';
import { allTabsQueryKey } from './allTabsQueryKey';

export interface DataUpdateDialogProps {
  accountId: string;
  authHeaders: Record<string, string>;
  tabId: string;
  tabName: string;
  /** 起動時点で表示していた公開 revision。BE の楽観ロックに使う。 */
  expectedActiveRevision: number;
  /** 開始（起動依頼の受付）成功時に、受け付けた内容を渡して呼ばれる。ダイアログを閉じるかは呼び出し元が決める。 */
  onStarted: (queued: QueuedDataUpdate) => void;
  onClose: () => void;
}

/** Agent によるデータ更新ジョブを開始するダイアログ。 */
export function DataUpdateDialog({
  accountId,
  authHeaders,
  tabId,
  tabName,
  expectedActiveRevision,
  onStarted,
  onClose,
}: DataUpdateDialogProps) {
  const queryClient = useQueryClient();
  const [autoActivate, setAutoActivate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const invalidateAllTabs = () =>
    queryClient.invalidateQueries({ queryKey: allTabsQueryKey(accountId) });

  const handleStart = async () => {
    setIsSubmitting(true);
    try {
      const started = await startCustomDashboardDataUpdate(
        accountId,
        tabId,
        { expectedActiveRevision, autoActivate },
        authHeaders,
      );
      await invalidateAllTabs();
      // ジョブの行は起動側（launcher Lambda）が作るため、一覧に現れるまで「受付中」と表示する。
      // 表示中の値ではなくサーバーが事前チェックで採用した baseRevision を示す。
      toast.success(`revision ${started.baseRevision} を元にデータ更新を受け付けました`);
      onStarted(started);
    } catch (error) {
      if (isRevisionConflict(error)) {
        await invalidateAllTabs();
        toast.error('他のユーザーが公開したため再読み込みします');
        onClose();
      } else if (isDataUpdateConflict(error)) {
        // 他ユーザーが直前に同タブでジョブを開始した可能性が高い。同様に揃える。
        await invalidateAllTabs();
        toast.error('このタブは更新中です');
      } else if (error instanceof ApiError && error.status === 400) {
        toast.error(error.message);
      } else if (isUnknownOutcome(error)) {
        // API Gateway の時間切れなどで応答が届かなかった。開始できている可能性があるため一覧で確かめてもらう。
        await invalidateAllTabs();
        toast.warning(
          'データ更新を開始できたか確認できませんでした。タブ一覧を再読み込みしたので、状態を確認してください',
        );
        onClose();
      } else {
        toast.error(error instanceof Error ? error.message : 'データ更新の開始に失敗しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tabName} のデータを更新</DialogTitle>
          <DialogDescription>
            details.txt の手順に従って Agent が data.json を再生成し、新しい revision
            として履歴に追加します。表示は公開するまで変わりません
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Checkbox
            id="data-update-auto-activate"
            checked={autoActivate}
            onCheckedChange={(checked) => setAutoActivate(checked === true)}
          />
          <Label htmlFor="data-update-auto-activate">完了後に自動公開</Label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            キャンセル
          </Button>
          <Button onClick={() => void handleStart()} disabled={isSubmitting}>
            {isSubmitting ? '実行中...' : '実行'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
